/**
 * @file MoneyCells.tsx
 * @brief The amount and the status of a billed row, the same on every web
 *        screen (18-10-2026): collected amount with "billed £X" when they
 *        differ, the money state, and "Deleted" / "Disputed" markers.
 *
 * @author DES
 * @date 18-10-2026
 */

import { Badge } from "@/components/ui/Badge";
import { formatMoneyMinor } from "@/shared/utils/formatters";
import { disputeLabel, moneyStatus, type MoneyRowLike } from "./moneyStatus";

export function MoneyAmount({ row, currency }: { row: MoneyRowLike; currency: string }) {
  const v = moneyStatus(row);
  return (
    <span className="inline-flex flex-col items-end tabular-nums">
      <span className={v.key === "refunded" ? "line-through decoration-muted-foreground/60" : undefined}>
        {formatMoneyMinor(v.shownCents, currency)}
      </span>
      {v.billedCents != null ? (
        <span className="text-xs text-muted-foreground">billed {formatMoneyMinor(v.billedCents, currency)}</span>
      ) : null}
      {v.key === "part_refunded" ? (
        <span className="text-xs text-muted-foreground">refunded {formatMoneyMinor(v.refundedCents, currency)}</span>
      ) : null}
    </span>
  );
}

export function MoneyBadges({ row, className }: { row: MoneyRowLike; className?: string }) {
  const v = moneyStatus(row);
  const dispute = disputeLabel(v.dispute);
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className ?? ""}`}>
      <Badge variant={v.tone}>{v.label}</Badge>
      {dispute ? <Badge variant={v.dispute === "won" ? "muted" : "destructive"}>{dispute}</Badge> : null}
      {v.deleted ? <Badge variant="muted">Deleted</Badge> : null}
    </span>
  );
}
